import { FC, useEffect, useMemo, useState } from 'react';
import { Card, Col, Image, Row, Select, Space, Tabs, Typography } from 'antd';
import { PhoneOutlined, MessageOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import placeholder from 'assets/img/placeholder.png';
import { LANGUAGES } from 'constants/profile';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuthStateContext } from 'providers/Auth';
import {
  DoctorProfileProvider,
  useDoctorProfile
} from 'providers/DoctorProfile';
import {
  DoctorSpecialtyRow,
  fetchDoctorSpecialties
} from 'services/api/profile';

import { Loading, PageWrapper } from 'components/ui';
import {
  Overview,
  Settings,
  WorkingHours,
  Plans,
  PaymentInfo
} from './components';
import { getFileUrl } from 'utils/file';
import { parseJson } from 'utils/json';
import { pickLocale } from 'utils/pickLocale';

const { Title, Paragraph } = Typography;
const { Option } = Select;

const ProfileContent: FC<{ lang: string }> = ({ lang }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id } = useParams();
  const { user: me } = useAuthStateContext();
  const { profile, loading } = useDoctorProfile();

  const [specialties, setSpecialties] = useState<DoctorSpecialtyRow[]>([]);

  useEffect(() => {
    fetchDoctorSpecialties()
      .then(setSpecialties)
      .catch(() => setSpecialties([]));
  }, []);

  const changeLocale = (val: string) => {
    searchParams.set('lang', val);
    setSearchParams(searchParams);
  };

  if (!me) return null;

  if (loading) {
    return <Loading />;
  }

  return (
    <PageWrapper
      title={`${me.first_name || ''} ${me.last_name || ''}`}
      extra={[
        <Select
          placeholder={t('doctor_profile.profile_language')}
          size="large"
          value={lang}
          onChange={val => changeLocale(val)}
          key="profile-lang-selector"
        >
          {LANGUAGES.map(item => (
            <Option
              key={`profile-lang-selector-${item.key}`}
              value={item.locale}
            >
              {item.label}
            </Option>
          ))}
        </Select>
      ]}
    >
      <Space size={32} direction="vertical" style={{ width: '100%' }}>
        <Card style={{ backgroundColor: 'transparent' }}>
          {me && (
            <Row justify="space-between">
              <Col>
                <Space size={16} align="start">
                  <Image
                    src={me.avatar ? getFileUrl(me.avatar) : placeholder}
                    width={240}
                    height={138}
                    preview={false}
                    style={{ objectFit: 'cover', borderRadius: 10 }}
                  />
                  <div>
                    <Space>
                      <Title level={4}>
                        {me.first_name} {me.last_name}
                      </Title>
                    </Space>
                    {!!specialties.length && (
                      <Paragraph>
                        {specialties
                          .map(item => pickLocale(item.specialty?.name, lang))
                          .filter(Boolean)
                          .join(', ')}
                      </Paragraph>
                    )}
                    {!!profile?.languages?.length && (
                      <Paragraph>
                        {parseJson(profile.languages)
                          ?.map((item: { label: string }) => item?.label)
                          .join(' | ')}
                      </Paragraph>
                    )}
                    {profile?.address && (
                      <Paragraph>{profile.address}</Paragraph>
                    )}
                  </div>
                </Space>
              </Col>
              <Col md={6}>
                <Space direction="vertical" size={50} style={{ width: '100%' }}>
                  <div>
                    {me.phone && (
                      <Paragraph>
                        <Space size={8}>
                          <PhoneOutlined />
                          {me.phone}
                        </Space>
                      </Paragraph>
                    )}
                    <Paragraph>
                      <Space size={8}>
                        <MessageOutlined />
                        {me.email}
                      </Space>
                    </Paragraph>
                  </div>
                </Space>
              </Col>
            </Row>
          )}
        </Card>
        <Card style={{ backgroundColor: 'transparent' }}>
          <Tabs
            defaultActiveKey={id}
            items={[
              {
                label: t('profile.overview'),
                key: 'overview',
                children: <Overview />
              },
              {
                label: t('profile.working_hours'),
                key: 'working-hours',
                children: <WorkingHours />
              },
              {
                label: t('profile.plans'),
                key: 'plans',
                children: <Plans />
              },
              {
                label: t('profile.settings'),
                key: 'settings',
                children: <Settings />
              },
              {
                label: t('profile.payment_info'),
                key: 'payment',
                children: <PaymentInfo />
              }
            ]}
          />
        </Card>
      </Space>
    </PageWrapper>
  );
};

const Profile: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const lang = useMemo(() => searchParams.get('lang'), [searchParams]);

  useEffect(() => {
    if (!lang) {
      searchParams.set('lang', 'en');
      setSearchParams(searchParams);
    }
  }, [lang, searchParams, setSearchParams]);

  if (!lang) return null;

  return (
    <DoctorProfileProvider lang={lang}>
      <ProfileContent lang={lang} />
    </DoctorProfileProvider>
  );
};

export default Profile;
